import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import UserHeader from "@/components/user/UserHeader";
import Footer from "@/components/layout/Footer";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useNavigate, useLocation } from "react-router-dom";
import { 
  Archive, 
  CheckCircle2, 
  Clock, 
  FileCheck, 
  BarChart4, 
  Award,
  CircleCheck,
  CircleSlash,
  Banknote,
  Home
} from "lucide-react";

const Retirement: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [projetosDialogOpen, setProjetosDialogOpen] = useState(false);

  // Funções para menu horizontal
  const isActivePath = (path: string): boolean => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    if (path === 'projects') {
      return ['/my-projects', '/project/status', '/project/region', '/all-projects'].some(
        p => location.pathname.startsWith(p)
      );
    }
    return location.pathname === path;
  };

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const handleProjetosClick = () => {
    setProjetosDialogOpen(true);
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <UserHeader />
      
      {/* Menu horizontal superior */}
      <div className="bg-white border-b border-gray-200 px-6">
        <div className="flex space-x-6 overflow-x-auto py-3">
          <button 
            className={`${isActivePath('/planos') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/planos')}
          >
            Planos
          </button>
          <button 
            className={`${isActivePath('/dashboard') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/dashboard')}
          >
            Dashboard
          </button>
          <button 
            className={`${isActivePath('projects') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={handleProjetosClick}
          >
            Projetos
          </button>
          <button 
            className={`${isActivePath('/emission') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/emission')}
          >
            Emissão
          </button>
          <button 
            className={`${isActivePath('/traceability') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/traceability')}
          >
            Rastreabilidade
          </button>
          <button 
            className={`${isActivePath('/certificates') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/certificates')}
          >
            Certificados
          </button>
          <button
            className={`${isActivePath('/pregao-dashboard') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/pregao-dashboard')}
          >
            Bolsa de Crédito de Carbono Voluntário
          </button>
          <button 
            className={`${isActivePath('/compliance') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/compliance')}
          >
            Conformidade
          </button>
          <button 
            className={`${isActivePath('/retirement') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/retirement')}
          >
            Aposentadoria
          </button>
          <button 
            className={`${isActivePath('/educational') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/educational')}
          >
            Espaço Educativo
          </button>
        </div>
      </div>
      
      {/* Pop-up de Acesso para Projetos */}
      <Dialog open={projetosDialogOpen} onOpenChange={setProjetosDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center text-xl">Acesso a Projetos</DialogTitle>
            <DialogDescription className="text-center">
              Selecione o tipo de visualização de projetos que deseja acessar.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            <div className="border border-gray-200 hover:border-[#0e7cb4] hover:shadow-md cursor-pointer transition-all rounded-lg" onClick={() => {
              setProjetosDialogOpen(false);
              handleNavigation('/my-projects');
            }}>
              <div className="p-6 flex flex-col items-center justify-center">
                <div className="bg-[#0e7cb4]/10 p-3 rounded-full mb-3">
                  <Home className="h-6 w-6 text-[#0e7cb4]" />
                </div>
                <h3 className="text-lg font-medium mb-1">Meus Projetos</h3>
                <p className="text-sm text-gray-500 text-center">Visualize todos os seus projetos cadastrados</p>
              </div>
            </div>
            <div className="border border-gray-200 hover:border-[#0e7cb4] hover:shadow-md cursor-pointer transition-all rounded-lg" onClick={() => {
              setProjetosDialogOpen(false);
              handleNavigation('/project/status');
            }}>
              <div className="p-6 flex flex-col items-center justify-center">
                <div className="bg-[#0e7cb4]/10 p-3 rounded-full mb-3">
                  <Home className="h-6 w-6 text-[#0e7cb4]" />
                </div>
                <h3 className="text-lg font-medium mb-1">Status dos Projetos</h3>
                <p className="text-sm text-gray-500 text-center">Acompanhe o status de todos os projetos</p>
              </div>
            </div>
            <div className="border border-gray-200 hover:border-[#0e7cb4] hover:shadow-md cursor-pointer transition-all rounded-lg" onClick={() => {
              setProjetosDialogOpen(false);
              handleNavigation('/project/region');
            }}>
              <div className="p-6 flex flex-col items-center justify-center">
                <div className="bg-[#0e7cb4]/10 p-3 rounded-full mb-3">
                  <Home className="h-6 w-6 text-[#0e7cb4]" />
                </div>
                <h3 className="text-lg font-medium mb-1">Projetos por Região</h3>
                <p className="text-sm text-gray-500 text-center">Explore projetos por localização geográfica</p>
              </div>
            </div>
            <div className="border border-gray-200 hover:border-[#0e7cb4] hover:shadow-md cursor-pointer transition-all rounded-lg" onClick={() => {
              window.open('https://banclima.com/projetos', '_blank');
              setProjetosDialogOpen(false);
            }}>
              <div className="p-6 flex flex-col items-center justify-center">
                <div className="bg-[#0e7cb4]/10 p-3 rounded-full mb-3">
                  <Home className="h-6 w-6 text-[#0e7cb4]" />
                </div>
                <h3 className="text-lg font-medium mb-1">Portal de Projetos</h3>
                <p className="text-sm text-gray-500 text-center">Acesse o portal público de projetos do Banclima</p>
              </div>
            </div>
          </div>
          <DialogFooter className="sm:justify-center">
            <button 
              className="px-4 py-2 rounded border border-gray-300 bg-white hover:bg-gray-100 text-gray-700 font-medium"
              onClick={() => setProjetosDialogOpen(false)}
            >
              Cancelar
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <div className="container mx-auto px-4 py-6 flex-grow">
        <Tabs defaultValue="info" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="info">Informações</TabsTrigger>
            <TabsTrigger value="consulta">Consulta de Crédito de Carbono Aposentado</TabsTrigger>
          </TabsList>
          <TabsContent value="info">
            <div className="bg-white p-6 rounded-lg shadow-md mb-6">
              <div className="flex items-center space-x-3 mb-4">
                <Archive className="h-8 w-8 text-[#0e7cb4]" />
                <h1 className="text-2xl font-bold text-[#0e7cb4]">Aposentadoria de Créditos de Carbono</h1>
              </div>
              
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
                <p className="text-gray-700">
                  A aposentadoria de crédito de carbono (em inglês, carbon credit retirement) é o processo pelo qual um crédito de carbono é retirado permanentemente 
                  de circulação, ou seja, não pode mais ser negociado ou reutilizado. Isso ocorre quando o crédito é usado para compensar uma emissão real de gases de efeito estufa.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <Card className="overflow-hidden">
                  <CardHeader className="bg-[#0e7cb4] text-white pb-2">
                    <div className="flex justify-between items-center">
                      <CardTitle className="text-lg flex items-center">
                        <Clock className="h-5 w-5 mr-2" />
                        Como Funciona a Aposentadoria
                      </CardTitle>
                      <Badge className="bg-white text-[#0e7cb4] hover:bg-gray-100">Processo</Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <ol className="space-y-4">
                      <li className="flex items-start">
                        <div className="bg-blue-100 rounded-full w-6 h-6 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                          <span className="text-[#0e7cb4] font-semibold">1</span>
                        </div>
                        <p className="text-gray-700">
                          <strong>Compra do Crédito:</strong> Uma pessoa, empresa ou instituição adquire créditos de carbono.
                        </p>
                      </li>
                      
                      <li className="flex items-start">
                        <div className="bg-blue-100 rounded-full w-6 h-6 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                          <span className="text-[#0e7cb4] font-semibold">2</span>
                        </div>
                        <p className="text-gray-700">
                          <strong>Declaração de Compensação:</strong> A entidade decide usar esses créditos para compensar suas emissões.
                        </p>
                      </li>
                      
                      <li className="flex items-start">
                        <div className="bg-blue-100 rounded-full w-6 h-6 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                          <span className="text-[#0e7cb4] font-semibold">3</span>
                        </div>
                        <p className="text-gray-700">
                          <strong>Aposentadoria no Registro:</strong> O crédito é marcado como "retirado" ou "aposentado" nos registros 
                          públicos ou blockchains, garantindo que não possa ser vendido ou usado novamente.
                        </p>
                      </li>
                      
                      <li className="flex items-start">
                        <div className="bg-blue-100 rounded-full w-6 h-6 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                          <span className="text-[#0e7cb4] font-semibold">4</span>
                        </div>
                        <p className="text-gray-700">
                          <strong>Certificado de Compensação:</strong> É comum que o aposentador receba um certificado 
                          de compensação com o número do crédito, data, origem do projeto, e volume de CO₂ compensado.
                        </p>
                      </li>
                    </ol>
                  </CardContent>
                </Card>
                
                <Card className="overflow-hidden">
                  <CardHeader className="bg-[#0e7cb4] text-white pb-2">
                    <div className="flex justify-between items-center">
                      <CardTitle className="text-lg flex items-center">
                        <FileCheck className="h-5 w-5 mr-2" />
                        Exemplo no Contexto do Banclima
                      </CardTitle>
                      <Badge className="bg-white text-[#0e7cb4] hover:bg-gray-100">Caso de Uso</Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <ul className="space-y-3">
                        <li className="flex items-center">
                          <CheckCircle2 className="h-5 w-5 text-green-600 mr-2 flex-shrink-0" />
                          <p className="text-gray-700"><strong>Projeto:</strong> "Reserva Legal Amazônia"</p>
                        </li>
                        <li className="flex items-center">
                          <CheckCircle2 className="h-5 w-5 text-green-600 mr-2 flex-shrink-0" />
                          <p className="text-gray-700"><strong>Token ID:</strong> TOKEN456</p>
                        </li>
                        <li className="flex items-center">
                          <CheckCircle2 className="h-5 w-5 text-green-600 mr-2 flex-shrink-0" />
                          <p className="text-gray-700"><strong>CPF Aposentador:</strong> 987.654.321-00</p>
                        </li>
                        <li className="flex items-center">
                          <CheckCircle2 className="h-5 w-5 text-green-600 mr-2 flex-shrink-0" />
                          <p className="text-gray-700"><strong>Data de Aposentadoria:</strong> 2025-04-19</p>
                        </li>
                        <li className="flex items-center">
                          <CheckCircle2 className="h-5 w-5 text-green-600 mr-2 flex-shrink-0" />
                          <p className="text-gray-700"><strong>Tipo:</strong> Compensação</p>
                        </li>
                        <li className="flex items-center">
                          <CheckCircle2 className="h-5 w-5 text-green-600 mr-2 flex-shrink-0" />
                          <p className="text-gray-700"><strong>Estado:</strong> Retirado da circulação (aposentado)</p>
                        </li>
                        <li className="flex items-center">
                          <CheckCircle2 className="h-5 w-5 text-green-600 mr-2 flex-shrink-0" />
                          <p className="text-gray-700"><strong>Quantidade:</strong> 200 MSCs = 200 KgCO₂e</p>
                        </li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              <div className="mb-8">
                <Card>
                  <CardHeader className="pb-2 bg-green-50">
                    <CardTitle className="text-lg text-green-800 flex items-center">
                      <BarChart4 className="h-5 w-5 mr-2" />
                      Importância da Aposentadoria de Créditos
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                        <div className="flex items-center mb-3">
                          <div className="bg-green-100 p-2 rounded-full mr-3">
                            <CircleSlash className="h-5 w-5 text-green-600" />
                          </div>
                          <h3 className="text-lg font-medium text-gray-800">Evita dupla contagem</h3>
                        </div>
                        <p className="text-gray-600">
                          Garante que um crédito não seja usado por mais de uma entidade, mantendo a integridade do sistema de compensação.
                        </p>
                      </div>
                      
                      <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                        <div className="flex items-center mb-3">
                          <div className="bg-green-100 p-2 rounded-full mr-3">
                            <Award className="h-5 w-5 text-green-600" />
                          </div>
                          <h3 className="text-lg font-medium text-gray-800">Gera confiança</h3>
                        </div>
                        <p className="text-gray-600">
                          Transparência e rastreabilidade nas transações de compensação de carbono aumentam a credibilidade do sistema como um todo.
                        </p>
                      </div>
                      
                      <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                        <div className="flex items-center mb-3">
                          <div className="bg-green-100 p-2 rounded-full mr-3">
                            <CircleCheck className="h-5 w-5 text-green-600" />
                          </div>
                          <h3 className="text-lg font-medium text-gray-800">Relaciona ações com metas</h3>
                        </div>
                        <p className="text-gray-600">
                          Empresas podem usar aposentadorias para cumprir metas de neutralidade de carbono e documentar seu progresso ambiental.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
            
            <TabsContent value="beneficios" className="mt-0">
              <h2 className="text-xl font-semibold mb-3 text-[#0e7cb4]">Benefícios para Empresas</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div className="flex items-start p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="bg-green-100 p-2 rounded-full mr-3">
                    <Banknote className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-800">Marketing Verde</h3>
                    <p className="text-gray-700 mt-1">
                      A aposentadoria de créditos permite que empresas demonstrem comprometimento ambiental tangível, 
                      melhorando sua imagem pública e atraindo consumidores ambientalmente conscientes.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="bg-green-100 p-2 rounded-full mr-3">
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-800">Conformidade Regulatória</h3>
                    <p className="text-gray-700 mt-1">
                      Em algumas jurisdições, a compensação de emissões através da aposentadoria de créditos 
                      pode ajudar empresas a cumprir requisitos regulatórios de redução de emissões.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="bg-green-100 p-2 rounded-full mr-3">
                    <BarChart4 className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-800">Relatórios ESG</h3>
                    <p className="text-gray-700 mt-1">
                      Fortalece os relatórios ambientais, sociais e de governança (ESG) da empresa, 
                      proporcionando métricas concretas de ação climática para investidores e stakeholders.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="bg-green-100 p-2 rounded-full mr-3">
                    <Award className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-800">Vantagem Competitiva</h3>
                    <p className="text-gray-700 mt-1">
                      Empresas que demonstram liderança em ações climáticas através da compensação de carbono 
                      ganham vantagem competitiva em mercados cada vez mais conscientes das questões ambientais.
                    </p>
                  </div>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="certificados" className="mt-0">
              <h2 className="text-xl font-semibold mb-3 text-[#0e7cb4]">Certificados de Compensação</h2>
              <p className="text-gray-700 mb-4">
                Os certificados de compensação de carbono emitidos após a aposentadoria de créditos são documentos importantes 
                que comprovam a ação climática tomada. Estes certificados geralmente contêm:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h3 className="text-lg font-medium text-gray-800 mb-2 flex items-center">
                    <span className="bg-blue-100 rounded-full w-6 h-6 flex items-center justify-center mr-2 flex-shrink-0">
                      <span className="text-[#0e7cb4] font-semibold">1</span>
                    </span>
                    Identificação do Crédito
                  </h3>
                  <p className="text-gray-700">
                    Informações sobre o número de série único do crédito, o registro em que foi emitido e o 
                    padrão de certificação usado (como VCS, Gold Standard, ou padrões nacionais).
                  </p>
                </div>
                
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h3 className="text-lg font-medium text-gray-800 mb-2 flex items-center">
                    <span className="bg-blue-100 rounded-full w-6 h-6 flex items-center justify-center mr-2 flex-shrink-0">
                      <span className="text-[#0e7cb4] font-semibold">2</span>
                    </span>
                    Origem do Projeto
                  </h3>
                  <p className="text-gray-700">
                    Detalhes sobre o projeto que gerou o crédito, incluindo localização, tipo de projeto 
                    (reflorestamento, energia renovável, etc.), e os benefícios adicionais que proporciona.
                  </p>
                </div>
                
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h3 className="text-lg font-medium text-gray-800 mb-2 flex items-center">
                    <span className="bg-blue-100 rounded-full w-6 h-6 flex items-center justify-center mr-2 flex-shrink-0">
                      <span className="text-[#0e7cb4] font-semibold">3</span>
                    </span>
                    Detalhes da Aposentadoria
                  </h3>
                  <p className="text-gray-700">
                    A data da aposentadoria, a quantidade exata de CO₂ compensada, o motivo da aposentadoria 
                    (como compensação corporativa ou pessoal), e o nome da entidade que aposentou os créditos.
                  </p>
                </div>
                
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h3 className="text-lg font-medium text-gray-800 mb-2 flex items-center">
                    <span className="bg-blue-100 rounded-full w-6 h-6 flex items-center justify-center mr-2 flex-shrink-0">
                      <span className="text-[#0e7cb4] font-semibold">4</span>
                    </span>
                    Verificação e Autenticidade
                  </h3>
                  <p className="text-gray-700">
                    Elementos que confirmam a autenticidade do certificado, como assinaturas digitais, códigos QR 
                    para verificação online, ou hashes de blockchain que vinculam o certificado aos registros públicos.
                  </p>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="blockchain" className="mt-0">
              <h2 className="text-xl font-semibold mb-3 text-[#0e7cb4]">Registro em Blockchain</h2>
              <p className="text-gray-700 mb-4">
                O uso de tecnologia blockchain para registro de aposentadoria de créditos de carbono representa 
                um avanço significativo na transparência e confiabilidade do sistema de compensação:
              </p>
              
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h3 className="text-lg font-medium text-blue-800 mb-2">Transparência Total</h3>
                  <p className="text-blue-700">
                    Os registros em blockchain são públicos e imutáveis, permitindo que qualquer pessoa verifique 
                    o histórico de um crédito desde sua criação até sua aposentadoria, eliminando preocupações sobre manipulação.
                  </p>
                </div>
                
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h3 className="text-lg font-medium text-blue-800 mb-2">Tokenização de Créditos</h3>
                  <p className="text-blue-700">
                    A representação de créditos de carbono como tokens digitais em uma blockchain facilita o 
                    rastreamento, a transferência e, eventualmente, a aposentadoria desses ativos, com todas as transações registradas de forma segura.
                  </p>
                </div>
                
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h3 className="text-lg font-medium text-blue-800 mb-2">Contratos Inteligentes</h3>
                  <p className="text-blue-700">
                    Smart contracts podem automatizar o processo de aposentadoria quando certas condições são atendidas, 
                    como a compra de um produto com compensação de carbono embutida ou o atingimento de uma meta organizacional.
                  </p>
                </div>
                
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h3 className="text-lg font-medium text-blue-800 mb-2">Verificação em Tempo Real</h3>
                  <p className="text-blue-700">
                    As partes interessadas podem verificar instantaneamente o status dos créditos aposentados, 
                    aumentando a confiança no processo de compensação e eliminando a necessidade de intermediários para confirmação.
                  </p>
                </div>
              </div>
            </TabsContent>
          </TabsContent>
          <TabsContent value="consulta">
            <Card className="mb-6">
              <CardContent className="p-6">
                <h2 className="text-lg font-semibold mb-4 text-[#0e7cb4]">Consulta de Crédito de Carbono Aposentado</h2>
                <div className="flex flex-col md:flex-row gap-4 items-center mb-4">
                  <input
                    type="text"
                    placeholder="Digite o número do crédito, projeto ou CPF/CNPJ"
                    className="border border-gray-300 rounded px-4 py-2 w-full md:w-1/2 focus:outline-none focus:ring-2 focus:ring-[#0e7cb4]"
                  />
                  <button className="bg-[#0e7cb4] text-white px-6 py-2 rounded hover:bg-[#095a7a] font-medium">Consultar</button>
                </div>
                <div className="text-gray-500 text-sm mb-6">Exemplo: 123456, Projeto Amazônia, 123.456.789-00</div>
                {/* Lista de créditos/lotes vendidos */}
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white border border-gray-200 rounded">
                    <thead>
                      <tr className="bg-[#f3f8fa]">
                        <th className="px-4 py-2 text-left text-[#0e7cb4] font-semibold">Lote</th>
                        <th className="px-4 py-2 text-left text-[#0e7cb4] font-semibold">Quantidade (KgCO2e)</th>
                        <th className="px-4 py-2 text-left text-[#0e7cb4] font-semibold">Data</th>
                        <th className="px-4 py-2 text-left text-[#0e7cb4] font-semibold">Comprador</th>
                        <th className="px-4 py-2 text-left text-[#0e7cb4] font-semibold">Documento</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { lote: 'LT-2025-010', quantidade: 500, data: '2024-06-01', comprador: 'Empresa Verde Ltda.', tipo: 'juridica', documento: '12.345.678/0001-99' },
                        { lote: 'LT-2025-009', quantidade: 500, data: '2024-05-28', comprador: 'Eco Carbono S.A.', tipo: 'juridica', documento: '98.765.432/0001-11' },
                        { lote: 'LT-2025-008', quantidade: 500, data: '2024-05-20', comprador: 'Compensar Brasil', tipo: 'juridica', documento: '11.222.333/0001-44' },
                        { lote: 'LT-2025-007', quantidade: 500, data: '2024-05-15', comprador: 'João da Silva', tipo: 'fisica', documento: '123.456.789-00' },
                        { lote: 'LT-2025-006', quantidade: 500, data: '2024-05-10', comprador: 'Maria Oliveira', tipo: 'fisica', documento: '987.654.321-00' },
                        { lote: 'LT-2025-005', quantidade: 500, data: '2024-05-05', comprador: 'Sustentare', tipo: 'juridica', documento: '55.666.777/0001-88' },
                        { lote: 'LT-2025-004', quantidade: 500, data: '2024-05-01', comprador: 'BioCompensa', tipo: 'juridica', documento: '22.333.444/0001-55' },
                        { lote: 'LT-2025-003', quantidade: 500, data: '2024-04-28', comprador: 'Carlos Souza', tipo: 'fisica', documento: '321.654.987-00' },
                        { lote: 'LT-2025-002', quantidade: 500, data: '2024-04-20', comprador: 'ClimaFácil', tipo: 'juridica', documento: '77.888.999/0001-22' },
                        { lote: 'LT-2025-001', quantidade: 500, data: '2024-04-15', comprador: 'EcoAção', tipo: 'juridica', documento: '33.444.555/0001-66' },
                      ].map((item, idx) => (
                        <tr key={item.lote} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-4 py-2 font-medium text-gray-700">{item.lote}</td>
                          <td className="px-4 py-2 text-gray-700">{item.quantidade}</td>
                          <td className="px-4 py-2 text-gray-700">{item.data}</td>
                          <td className="px-4 py-2 text-gray-700">{item.comprador}</td>
                          <td className="px-4 py-2 text-gray-700">{item.tipo === 'fisica' ? `CPF: ${item.documento}` : `CNPJ: ${item.documento}`}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      
      <Footer />
    </div>
  );
};

export default Retirement; 